import { API_CONFIG } from "@/config";
import {
  clearEvents,
  clientContext,
  getEvents,
  type ClientLogEntry,
} from "@services/clientLog";
import { useCallback, useEffect, useMemo, useState } from "react";

type ProbeStatus = "pending" | "ok" | "warn" | "fail";

interface ProbeResult {
  status: ProbeStatus;
  label: string;
  detail: string;
  ms?: number;
}

const PROBE_TIMEOUT_MS = 12000;

interface FetchProbe {
  ok: boolean;
  status: number;
  ms: number;
  contentType?: string;
  bodyStart?: string;
  error?: string;
}

async function timedFetch(url: string, mode: RequestMode): Promise<FetchProbe> {
  const started = performance.now();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      mode,
      cache: "no-store",
      signal: controller.signal,
    });
    const ms = Math.round(performance.now() - started);
    let bodyStart: string | undefined;
    if (mode !== "no-cors") {
      try {
        bodyStart = (await res.text()).slice(0, 80).replace(/\s+/g, " ").trim();
      } catch {
        /* opaque / unreadable body */
      }
    }
    return {
      ok: true,
      status: res.status,
      ms,
      contentType: res.headers.get("content-type") ?? undefined,
      bodyStart,
    };
  } catch (err) {
    const aborted = err instanceof DOMException && err.name === "AbortError";
    return {
      ok: false,
      status: 0,
      ms: Math.round(performance.now() - started),
      error: aborted
        ? `no response in ${PROBE_TIMEOUT_MS / 1000}s`
        : err instanceof Error
          ? err.message
          : "failed",
    };
  } finally {
    clearTimeout(timer);
  }
}

// Hits the API through the same origin (the Vercel proxy path). The content
// type is the tell: HTML means Vercel served the SPA for /api (the proxy is
// NOT routing); JSON means the request reached the backend.
async function probeBackend(): Promise<ProbeResult> {
  const url = `${window.location.origin}/api/v1/`;
  const res = await timedFetch(url, "cors");

  if (!res.ok) {
    return {
      status: "fail",
      label: "API proxy → backend",
      detail: `Unreachable: ${res.error}`,
      ms: res.ms,
    };
  }

  const looksHtml =
    (res.contentType ?? "").includes("text/html") ||
    (res.bodyStart ?? "").toLowerCase().startsWith("<!doctype");

  if (looksHtml) {
    return {
      status: "fail",
      label: "API proxy → backend",
      detail: `Proxy NOT applied — Vercel served the SPA (HTML) for /api, so the rewrite is not routing. HTTP ${res.status}.`,
      ms: res.ms,
    };
  }

  return {
    status: res.status < 500 ? "ok" : "warn",
    label: "API proxy → backend",
    detail: `Proxy OK — backend responded (HTTP ${res.status}, ${res.contentType ?? "unknown type"}).`,
    ms: res.ms,
  };
}

async function probeFonts(): Promise<ProbeResult> {
  const r = await timedFetch("https://fonts.gstatic.com/", "no-cors");
  return {
    status: r.ok ? "ok" : "warn",
    label: "Google Fonts",
    detail: r.ok ? "Reachable" : `Not reachable: ${r.error}`,
    ms: r.ms,
  };
}

const STATUS_STYLE: Record<ProbeStatus, string> = {
  pending: "bg-gray-100 text-gray-500",
  ok: "bg-green-100 text-green-700",
  warn: "bg-yellow-100 text-yellow-800",
  fail: "bg-red-100 text-red-700",
};

const STATUS_LABEL: Record<ProbeStatus, string> = {
  pending: "testing…",
  ok: "OK",
  warn: "warning",
  fail: "failed",
};

export default function DiagnosticsPage() {
  const ctx = useMemo(() => clientContext(), []);
  const [frontendProbe] = useState<ProbeResult>({
    status: "ok",
    label: "Frontend (Vercel)",
    detail: "Page loaded — bundle and edge reachable",
  });
  const [backend, setBackend] = useState<ProbeResult>({
    status: "pending",
    label: "API proxy → backend",
    detail: "",
  });
  const [fonts, setFonts] = useState<ProbeResult>({
    status: "pending",
    label: "Google Fonts",
    detail: "",
  });
  const [events, setEvents] = useState<ClientLogEntry[]>([]);
  const [running, setRunning] = useState(false);
  const [copied, setCopied] = useState(false);

  const runProbes = useCallback(async () => {
    setRunning(true);
    setBackend({ status: "pending", label: "API proxy → backend", detail: "" });
    setFonts({ status: "pending", label: "Google Fonts", detail: "" });
    const [b, f] = await Promise.all([probeBackend(), probeFonts()]);
    setBackend(b);
    setFonts(f);
    setEvents(getEvents());
    setRunning(false);
  }, []);

  useEffect(() => {
    runProbes();
  }, [runProbes]);

  const probes = [frontendProbe, backend, fonts];

  const report = useMemo(
    () =>
      JSON.stringify(
        {
          generatedAt: new Date().toISOString(),
          context: ctx,
          apiBaseUrl: API_CONFIG.baseUrl,
          probes: [frontendProbe, backend, fonts],
          events,
        },
        null,
        2,
      ),
    [ctx, frontendProbe, backend, fonts, events],
  );

  const copyReport = async () => {
    try {
      await navigator.clipboard.writeText(report);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — user can select the textarea manually */
    }
  };

  return (
    <div style={{ fontFamily: "system-ui, sans-serif" }} className="min-h-screen bg-gray-50 p-6 text-gray-900">
      <div className="mx-auto max-w-2xl">
        <h1 className="text-xl font-semibold">Connection diagnostics</h1>
        <p className="mt-1 text-sm text-gray-500">
          Independent of login and data. Open this URL from the location having
          trouble and send the result.
        </p>

        <div className="mt-6 space-y-2">
          {probes.map((p) => (
            <div
              key={p.label}
              className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3"
            >
              <div className="min-w-0">
                <div className="font-medium">{p.label}</div>
                {p.detail && (
                  <div className="truncate text-sm text-gray-500">{p.detail}</div>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {typeof p.ms === "number" && (
                  <span className="text-xs text-gray-400">{p.ms}ms</span>
                )}
                <span
                  className={`rounded-full px-2 py-1 text-xs font-medium ${STATUS_STYLE[p.status]}`}
                >
                  {STATUS_LABEL[p.status]}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={runProbes}
            disabled={running}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {running ? "Testing…" : "Run again"}
          </button>
          <button
            onClick={copyReport}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium"
          >
            {copied ? "Copied!" : "Copy report"}
          </button>
          <button
            onClick={() => {
              clearEvents();
              setEvents([]);
            }}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-500"
          >
            Clear log
          </button>
        </div>

        <h2 className="mt-8 text-sm font-semibold uppercase tracking-wide text-gray-500">
          Environment
        </h2>
        <dl className="mt-2 grid grid-cols-1 gap-1 rounded-lg border border-gray-200 bg-white p-4 text-sm sm:grid-cols-2">
          <Info k="Time zone" v={ctx.timezone} />
          <Info k="Offset (min)" v={String(ctx.offsetMinutes)} />
          <Info k="Language" v={ctx.locale} />
          <Info k="Online" v={ctx.online ? "yes" : "no"} />
          <Info k="API" v={API_CONFIG.baseUrl} />
          <Info k="UA" v={ctx.userAgent} />
        </dl>

        <h2 className="mt-8 text-sm font-semibold uppercase tracking-wide text-gray-500">
          Recent events ({events.length})
        </h2>
        {events.length === 0 ? (
          <p className="mt-2 text-sm text-gray-400">No errors recorded.</p>
        ) : (
          <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-3 py-2">Time</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Message</th>
                  <th className="px-3 py-2">HTTP</th>
                  <th className="px-3 py-2">ms</th>
                </tr>
              </thead>
              <tbody>
                {events
                  .slice()
                  .reverse()
                  .map((e, i) => (
                    <tr key={i} className="border-t border-gray-100 align-top">
                      <td className="px-3 py-2 whitespace-nowrap text-gray-400">
                        {e.t.slice(11, 19)}
                      </td>
                      <td className="px-3 py-2">{e.kind}</td>
                      <td className="px-3 py-2">
                        <div>{e.msg}</div>
                        {e.detail && (
                          <div className="text-gray-400">{e.detail}</div>
                        )}
                      </td>
                      <td className="px-3 py-2">{e.status ?? e.code ?? ""}</td>
                      <td className="px-3 py-2">{e.ms ?? ""}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex gap-2 break-all">
      <dt className="shrink-0 text-gray-400">{k}:</dt>
      <dd className="font-medium">{v}</dd>
    </div>
  );
}
